import React from 'react';
import {
  ArrowRight,
  Users,
  Newspaper,
  MessageSquare,
  Twitter,
  Youtube,
  Check,
  Download,
  Menu,
  X,
} from 'lucide-react';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  const heroImages = [
    'portrait1.png',
    'portrait2.png',
    'portrait3.png',
  ];

  // Auto-rotate hero images every 5 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % heroImages.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const scrollToSection = (sectionId) => {
    document
      .getElementById(sectionId)
      ?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <div className="w-screen min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 w-full bg-black/20 backdrop-blur-lg z-50 border-b border-white/10">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center space-x-3">
              <img
                src="/app_icon.png"
                alt="Ekşpress Logo"
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-2xl font-bold">Ekşpress</span>
            </div>

            <div className="hidden md:flex space-x-8">
              <button
                onClick={() => scrollToSection('features')}
                className="hover:text-purple-400 transition-colors text-lg font-medium"
              >
                Özellikler
              </button>
              <button
                onClick={() => scrollToSection('screenshots')}
                className="hover:text-purple-400 transition-colors text-lg font-medium"
              >
                Ekran Görüntüleri
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="hover:text-purple-400 transition-colors text-lg font-medium"
              >
                Hakkında
              </button>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
              <a
                href="https://apps.apple.com/us/app/ek%C5%9Fpress/id6743127797"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:block bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 rounded-full font-semibold text-lg hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 text-white no-underline"
              >
                App Store'dan İndir
              </a>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-black/90 backdrop-blur-lg border-b border-white/10">
              <div className="px-4 py-6 space-y-4">
                <button
                  onClick={() => scrollToSection('features')}
                  className="block w-full text-left hover:text-purple-400 transition-colors text-lg font-medium py-2"
                >
                  Özellikler
                </button>
                <button
                  onClick={() => scrollToSection('screenshots')}
                  className="block w-full text-left hover:text-purple-400 transition-colors text-lg font-medium py-2"
                >
                  Ekran Görüntüleri
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="block w-full text-left hover:text-purple-400 transition-colors text-lg font-medium py-2"
                >
                  Hakkında
                </button>
                <a
                  href="https://apps.apple.com/us/app/ek%C5%9Fpress/id6743127797"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 rounded-full font-semibold text-lg hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 mt-4 text-white no-underline block text-center"
                >
                  App Store'dan İndir
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="w-full min-h-screen flex items-center justify-center relative">
        {/* Background Elements */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500 rounded-full opacity-20 blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-500 rounded-full opacity-10 blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="w-full px-4 sm:px-6 lg:px-8 py-32 text-center relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Hero Content */}
              <div className="space-y-8 text-center lg:text-left">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
                  <span className="text-lg font-medium">
                    🚀 Şimdi Beta'da
                  </span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent leading-tight">
                  Gündem Takibi
                  <br />
                  <span className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl">
                    Hiç Bu Kadar Basit Olmamıştı
                  </span>
                </h1>

                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 max-w-2xl lg:max-w-3xl mx-auto lg:mx-0 leading-relaxed">
                  Günlük haberleri özetleriyle takip et, Ekşi Sözlük
                  yorumlarını ve sosyal medya tepkilerini bir arada
                  gör
                </p>

                <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start items-center pt-8">
                  <a
                    href="https://apps.apple.com/us/app/ek%C5%9Fpress/id6743127797"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 px-8 py-4 rounded-full font-semibold text-lg hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 flex items-center justify-center space-x-3 text-white no-underline"
                  >
                    <Download className="w-5 h-5" />
                    <span>App Store'dan İndir</span>
                  </a>
                  <button
                    onClick={() => scrollToSection('features')}
                    className="w-full sm:w-auto border-2 border-white/30 px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all flex items-center justify-center space-x-3"
                  >
                    <span>Özellikler</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Hero Phone Mockup */}
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  {/* Phone Container */}
                  <div className="w-72 h-[600px] lg:w-80 lg:h-[660px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-[3rem] p-2 shadow-2xl">
                    <div className="w-full h-full bg-gray-700 rounded-[2.5rem] overflow-hidden relative">
                      {/* Notch */}
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-10"></div>
                      {/* Screenshot */}
                      <img
                        src={`/${heroImages[currentImageIndex]}`}
                        alt="Ekşpress App Screenshot"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback if image doesn't exist yet
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Placeholder when image is not available */}
                      <div
                        className="w-full h-full bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center text-center p-8"
                        style={{ display: 'none' }}
                      >
                        <div>
                          <img
                            src="/app_icon.png"
                            alt="Ekşpress Logo"
                            className="w-16 h-16 rounded-xl mx-auto mb-4"
                          />
                          <p className="text-white text-lg font-semibold mb-2">
                            Ekşpress
                          </p>
                          <p className="text-gray-300 text-sm">
                            Uygulama Ekran Görüntüsü
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-[3rem] blur-2xl -z-10 scale-110"></div>

                  {/* Floating Elements */}
                  <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-bounce delay-1000"></div>
                  <div className="absolute -bottom-6 -left-6 w-6 h-6 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full animate-bounce delay-500"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="w-full py-20 lg:py-32 bg-gradient-to-b from-transparent to-black/20"
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-300">
                Özellikler
              </h2>
              <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
                Ekşpress ile haberleri takip etmek, sosyal medya
                tepkilerini görmek ve Ekşi Sözlük okumak hiç bu kadar
                kolay olmamıştı
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <Newspaper className="w-8 h-8" />,
                  title: 'Günlük Haber Özetleri',
                  description:
                    'Günün öne çıkan 10 haberini yapay zeka ile özetlenen kısa metinlerle takip et',
                  color: 'from-blue-500 to-cyan-500',
                },
                {
                  icon: <Users className="w-8 h-8" />,
                  title: 'Sosyal Medya Entegrasyonu',
                  description:
                    'Haberlere yönelik Ekşi Sözlük, YouTube ve X (Twitter) tepkilerini tek yerden gör',
                  color: 'from-purple-500 to-pink-500',
                },
                {
                  icon: <MessageSquare className="w-8 h-8" />,
                  title: 'Ekşi Sözlük Okuyucusu',
                  description:
                    'Başlık arama, entry okuma, bugün olanlar ve daha fazlası. Tam teşekküllü sözlük deneyimi',
                  color: 'from-green-500 to-emerald-500',
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 transform hover:scale-105 border border-white/10"
                >
                  <div
                    className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6`}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section
        id="screenshots"
        className="w-full py-20 lg:py-32 bg-black/10"
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-300">
                Ekran Görüntüleri
              </h2>
              <p className="text-lg sm:text-xl text-gray-300">
                Uygulamanın kullanım deneyimine göz atın
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              {[
                {
                  title: 'Haber Özetleri',
                  description:
                    'Günün öne çıkan haberlerini kısa özetleriyle görüntüle',
                },
                {
                  title: 'Sosyal Medya Tepkileri',
                  description:
                    'Haberlere yönelik sosyal medya paylaşımları',
                },
                {
                  title: 'Ekşi Sözlük Okuyucu',
                  description: 'Başlık arama ve entry okuma deneyimi',
                },
              ].map((screenshot, index) => (
                <div key={index} className="text-center">
                  <div className="relative mb-6">
                    {/* Container with proper aspect ratio for 2860x1320 images */}
                    <div className="w-full max-w-sm mx-auto aspect-[2.17/1] bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-3 shadow-2xl overflow-hidden">
                      <img
                        src={`/${index + 1}.png`}
                        alt={screenshot.title}
                        className="w-full h-full object-contain rounded-xl bg-black"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl -z-10"></div>
                  </div>
                  <h3 className="text-xl font-bold mb-2">
                    {screenshot.title}
                  </h3>
                  <p className="text-gray-300 text-sm">
                    {screenshot.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Detail */}
      <section className="w-full py-20 lg:py-32">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-300">
                  Gündem Takibi Artık Çok Kolay
                </h2>
                <div className="space-y-6">
                  {[
                    'Günün öne çıkan 10 haberini yapay zeka ile özetliyoruz',
                    'Haberlerin kaynaklarını gösteriyoruz',
                    'Sosyal medya tepkilerini tek yerden izleyebiliyorsunuz',
                    'Ekşi Sözlük entrylerini okuyabiliyorsunuz',
                    'Temiz arayüz, kolay kullanım',
                  ].map((point, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4" />
                      </div>
                      <p className="text-lg text-gray-300">{point}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="w-80 h-96 mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-6 shadow-2xl">
                  <div className="w-full h-full bg-gray-700 rounded-2xl flex items-center justify-center">
                    <div className="text-center">
                      <div className="flex space-x-2 mb-4 justify-center">
                        <Youtube className="w-8 h-8 text-red-500" />
                        <Twitter className="w-8 h-8 text-blue-500" />
                        <MessageSquare className="w-8 h-8 text-green-500" />
                      </div>
                      <p className="text-gray-400 text-sm">
                        Sosyal Medya Entegrasyonu
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="w-full py-20 lg:py-32 bg-black/10"
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-8 text-gray-300">
              Neden Ekşpress?
            </h2>
            <div className="text-lg text-gray-300 space-y-6 leading-relaxed">
              <p>
                Kompleks haber uygulamalarından bıkan ve gündem
                takibini basitleştirmek isteyen kullanıcılar için
                geliştirildi. Hem haber özetleri hem de sosyal medya
                tepkilerini tek yerden görmenizi sağlar.
              </p>
              <p>
                Ekşi Sözlük'ün mobil uygulamasını kapatması sonrası,
                sözlük okuyucuları için tam teşekküllü bir çözüm
                sunuyor. Başlık arama, entry okuma, bugün olanlar ve
                daha fazlası.
              </p>
              <p>
                Sade tasarım. Sadece gündem takibi ve sözlük okuma
                için basit, kullanışlı bir uygulama.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-20 lg:py-32">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl p-12 lg:p-16 text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-300">
                Ekşpress'i Deneyin
              </h2>
              <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto">
                Beta sürümünü indirip geri bildirimlerinizi paylaşın.
                Uygulamayı birlikte daha da iyi hale getirelim.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a
                  href="https://apps.apple.com/us/app/ek%C5%9Fpress/id6743127797"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 no-underline"
                >
                  <Download className="w-5 h-5" />
                  <span>App Store'dan İndir</span>
                </a>
                <a
                  href="mailto:info@noblapps.io"
                  className="border-2 border-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all flex items-center justify-center space-x-2"
                >
                  <span>Geri Bildirim Gönder</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-12 border-t border-white/10 bg-black/10">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <img
                  src="/app_icon.png"
                  alt="Ekşpress Logo"
                  className="w-10 h-10 rounded-xl"
                />
                <span className="text-2xl font-bold">ekşpress</span>
              </div>

              <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8">
                <div className="flex items-center space-x-6">
                  <a
                    href="/privacy-policy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    Gizlilik Politikası
                  </a>
                  <a
                    href="mailto:info@noblapps.io"
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    info@noblapps.io
                  </a>
                </div>
                <a
                  href="https://apps.apple.com/us/app/ek%C5%9Fpress/id6743127797"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 text-white no-underline"
                >
                  App Store'dan İndir
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
